// We will use these to find the nav links and mega menu templates
const identifiers = {
  nav: 'NavLink_',
  megamenu: 'MegaMenu_',
  item: 'MegaMenuItem_',
  header: 'MegaMenuHeader_',
  children: 'MegaMenuChildren_'
};

// Store all the timeouts here
const timeouts = {};

// Find the header element
const header = document.querySelector('.js-header');
// Find the nav element
const nav = document.querySelector('.js-nav');
// Store all the nav links here
const navLinks = [...nav.querySelectorAll(`[id^="${identifiers.nav}"]`)];
// Store the activeLink here
let activeLink = null;

// Find the mega menu elements
const megamenu = document.querySelector('.js-mega-menu');
// Find the mega menu container
const megamenuContainer = megamenu.querySelector('.js-mega-menu__container');
// Find all the mega menu templates
const megamenuTemplates = [...megamenu.querySelectorAll(`[id^="${identifiers.megamenu}"]`)];

// ----------------------------------------------------------------------------------------------------
// Functions
// ----------------------------------------------------------------------------------------------------

let megaMenuPromise = Promise.resolve(); // Initial promise that resolves immediately

function initMenuOverflow(menu) {
  let elements = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : [];
  const bottomPadding = 40;
  const maxHeight = window.innerHeight - menu.getBoundingClientRect().top - bottomPadding || 0;
  elements.forEach(element => {
    if (!element) return;
    element.style.maxHeight = `${maxHeight}px`;
  });
}
function transitionMegaMenu(action, template) {
  // Wrap the transition logic in a new promise
  megaMenuPromise = megaMenuPromise.then(() => {
    return new Promise(resolve => {
      if (action === 'close') {
        // Clear all the timeouts
        clearTimeout(timeouts['open']);
        clearTimeout(timeouts['close']);

        // Remove the open class from the mega menu
        megamenu.classList.remove('is-open');

        // Empty the mega menu container after a delay
        timeouts['close'] = setTimeout(() => {
          megamenuContainer.innerHTML = '';

          // Resolve the promise after the action completes
          resolve();
        }, 150);
      } else if (action === 'open' && typeof template !== 'undefined') {
        // Clear all the timeouts
        clearTimeout(timeouts['open']);
        clearTimeout(timeouts['close']);

        // Add the mega menu template's innerHTML to the mega menu container
        megamenuContainer.innerHTML = template.innerHTML;
        timeouts['open'] = setTimeout(() => {
          // Add the open class to the mega menu
          megamenu.classList.add('is-open');

          // Resolve the promise after the action completes
          resolve();
        }, 50);
      }
    });
  });
}
function closeMegaMenu() {
  transitionMegaMenu('close');
}
function openMegaMenu(template) {
  transitionMegaMenu('open', template);
}
function clearActiveLinks() {
  if (activeLink) {
    activeLink.focus();
    activeLink = null;
  }
  ;
  navLinks.forEach(link => {
    link.classList.remove('active');
    link.ariaExpanded = false;
  });
}
;
function trapFocus() {
  let items = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : [];
  const firstItem = items[0];
  const lastItem = items[items.length - 1];
  if (!firstItem || !lastItem) return;
  items.forEach((item, index) => {
    // On arrow down, move to the next item or the first item
    item.addEventListener('keydown', e => {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        const nextItem = items[index + 1] || firstItem;
        nextItem.focus();
      }
    });

    // On arrow up, move to the previous item or the last item
    item.addEventListener('keydown', e => {
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        const previousItem = items[index - 1] || lastItem;
        previousItem.focus();
      }
    });
  });
  lastItem.addEventListener('keydown', e => {
    if (e.key === 'Tab' || e.keyCode === 9) {
      if (!e.shiftKey) {
        e.preventDefault();
        firstItem.focus();
      }
    }
  });
  firstItem.addEventListener('keydown', e => {
    if (e.key === 'Tab' || e.keyCode === 9) {
      if (e.shiftKey) {
        e.preventDefault();
        lastItem.focus();
      }
    }
  });
}

// ----------------------------------------------------------------------------------------------------
// Initialise the main nav links so that they open their corresponding mega menu templates when clicked
// ----------------------------------------------------------------------------------------------------

// Loop all the nav links
navLinks.forEach(navLink => {
  // Get the nav link id without the identifier
  const ID = navLink.id.replace(identifiers.nav, '');
  // Find the corresponding mega menu template
  const template = megamenuTemplates.find(template => template.id === `${identifiers.megamenu}${ID}`);
  const open = () => {
    // Determine if the nav link is already active
    let wasActive = navLink.classList.contains('active');

    // If the menu is already open, close it
    if (megamenu.classList.contains('is-open')) closeMegaMenu();

    // Remove the active state on all nav links
    clearActiveLinks();

    // If the nav link was active, then return here
    if (wasActive) return;

    // Add the active state to the nav link
    navLink.classList.add('active');
    navLink.ariaExpanded = true;

    // Set the active link
    activeLink = navLink;

    // Open the mega menu
    openMegaMenu(template);
  };

  // If the mega menu template exists
  if (template) {
    // Add the event listener to the nav link
    navLink.addEventListener('click', e => {
      e.preventDefault();
      open();
    });
    navLink.addEventListener('keydown', e => {
      // If the user hits the right arrow key, open the mega menu
      if (e.key === 'ArrowRight') {
        e.preventDefault();
        open();
      }

      // If the user hits enter, go to the link
      if (e.key === 'Enter') {
        e.preventDefault();
        // Go to the link
        window.location.href = navLink.href;
      }
    });

    // If we are focused on the nav link, that means the menu is not open
    navLink.addEventListener('focus', () => navLink.ariaExpanded = false);
  }
});

// If the user clicks outside the mega menu, close it
megamenu.addEventListener('click', e => {
  if (e.target === megamenu) {
    closeMegaMenu();
    clearActiveLinks();
  }
});
window.addEventListener('keydown', e => {
  if (e.key === 'Escape') {
    closeMegaMenu();
    clearActiveLinks();
  }
});

// ----------------------------------------------------------------------------------------------------
// Watch for any new mega menus to be added to the page so we can initialise them
// ----------------------------------------------------------------------------------------------------

// Watch for any new mega menu templates to be added to the page
const MegaMenuWatcher = new MutationObserver(() => {
  // Find the new mega menu menu
  const menu = megamenuContainer.querySelector('.js-mega-menu__menu');

  // Do nothing if the menu doesn't exist
  if (!menu) return;

  // Find the menu header
  const menuHeader = menu.querySelector('.js-mega-menu__header');
  // Find the main links
  const menuMain = menu.querySelector('.js-mega-menu__main');
  // Find the menu children
  const menuChildren = menu.querySelector('.js-mega-menu__children');

  // Find the menu items
  const menuItems = [...menu.querySelectorAll(`[id^="${identifiers.item}"]`)];
  // Find the childrenTemplate
  const childrenTemplates = [...menu.querySelectorAll(`[id^="${identifiers.children}"]`)];
  // Find the menu headers
  const headerTemplates = [...menu.querySelectorAll(`[id^="${identifiers.header}"]`)];
  initMenuOverflow(menu, [menuMain, menuChildren, menuHeader]);

  // window.addEventListener('resize', () => initMenuOverflow([menuChildren, menuHeader]));

  const focus = (item, header, children) => {
    item.focus();
    // Set the active class on the menu items
    menuItems.forEach(menuItem => {
      menuItem.classList.toggle('active', menuItem === item);

      // Make sure the menu item has aria-expanded before toggling it
      if (menuItem.getAttribute('aria-expanded')) {
        menuItem.ariaExpanded = menuItem === item;
      }
    });

    // Set the menu header and children
    if (header) {
      if (menuHeader.innerHTML != header.innerHTML) {
        menuHeader.innerHTML = header ? header.innerHTML : '';
        menuHeader.scrollTop = 0;
      }
    } else {
      menuHeader.innerHTML = '';
    }
    if (children) {
      if (menuChildren.innerHTML != children.innerHTML) {
        menuChildren.innerHTML = children ? children.innerHTML : '';
        menuChildren.scrollTop = 0;
      }
    } else {
      menuChildren.innerHTML = '';
    }

    // Find the next link
    const nextLinks = [...menuChildren.querySelectorAll('a')];
    nextLinks.forEach(link => {
      link.addEventListener('keydown', e => {
        if (e.key === 'ArrowLeft') {
          // Move the focus to the menu item
          item.focus();
        }
      });
    });
    trapFocus(nextLinks);
    item.addEventListener('keydown', e => {
      // If there is a next link
      if (nextLinks.length) {
        if (e.key === 'ArrowRight') {
          // Move the focus to the first child in the menuChildren
          nextLinks[0].focus();
        }
      }
    });
  };

  // Loop all the menu items
  menuItems.forEach((menuItem, index) => {
    // Get the menu item id without the identifier
    const ID = menuItem.id.replace(identifiers.item, '');
    // Find the corresponding children template
    const childrenTemplate = childrenTemplates.find(template => template.id === `${identifiers.children}${ID}`);
    // Find the corresponding header template
    const headerTemplate = headerTemplates.find(template => template.id === `${identifiers.header}${ID}`);
    if (index === 0) {
      menuItem.focus();
      // Focus the first menu item
      focus(menuItem, headerTemplate, childrenTemplate);
    }
    menuItem.addEventListener('keydown', e => {
      if (e.key === 'ArrowLeft') {
        // Close the mega menu
        closeMegaMenu();
        clearActiveLinks();
      }
    });

    // When the menu item is hovered or focused
    ['mouseover', 'focus'].forEach(event => {
      menuItem.addEventListener(event, () => {
        // Focus that link
        focus(menuItem, headerTemplate, childrenTemplate);
      });
    });
  });
  trapFocus(menuItems);
});

// Observe the mega menu
MegaMenuWatcher.observe(megamenuContainer, {
  childList: true
});